import React, { useMemo } from 'react';
import { Table } from 'antd';
import { HistoryOutlined } from '@ant-design/icons';
import moment from 'moment';

/**
 * VisitHistoryTab
 * Dedicated tab showing summary table of all visits (including current) with vital/lab snapshot.
 * Props:
 *  - visits: array of visit objects
 *  - currentVisitId: id of active editable visit
 *  - onSelectVisit: function(visitId) invoked when user clicks a historical row
 */
const VisitHistoryTab = ({ visits = [], currentVisitId, onSelectVisit }) => {
  const data = useMemo(() => visits.map(visit => {
    const latestVitalSign = visit.vitalSigns?.[visit.vitalSigns.length - 1];
    const latestBloodTest = visit.bloodTests?.[0];
    return {
      key: visit.visitId,
      visitId: visit.visitId,
      date: visit.visitDate,
      location: visit.location,
      event: visit.eventName,
      bp: latestVitalSign ? `${latestVitalSign.systolicBP}/${latestVitalSign.diastolicBP}` : '-',
      hr: latestVitalSign?.heartRate || '-',
      cholesterol: latestBloodTest?.totalCholesterol || '-',
      hdl: latestBloodTest?.hdlCholesterol || '-',
      ldl: latestBloodTest?.ldlCholesterol || '-',
      a1c: latestBloodTest?.a1c || '-',
      // status removed – active differentiation no longer displayed
    };
  }), [visits, currentVisitId]);

  return (
    <div style={{ paddingTop: 8 }}>
      <style>{`
        .visit-history-table .clickable-history-row { cursor: pointer; }
        .visit-history-table .clickable-history-row:hover td { background: #f5faff !important; }
      `}</style>
      <div className="visit-history-header" style={{ fontSize: 16, fontWeight: 600, marginBottom: 16, display:'flex', alignItems:'center', background:'#fff', padding:'4px 0' }}>
        <HistoryOutlined style={{ marginRight: 8 }} />
        Visit History Summary ({visits.length} {visits.length === 1 ? 'Visit' : 'Visits'})
      </div>
      <Table
        className="visit-history-table"
        dataSource={data}
        columns={[
          {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            render: (date) => moment(date).format('MMM DD, YYYY'),
            sorter: (a, b) => moment(a.date).unix() - moment(b.date).unix(),
            defaultSortOrder: 'descend',
            width: 130
          },
          {
            title: 'Time',
            key: 'time',
            render: (_, record) => record.date ? moment(record.date).format('h:mm A') : '-',
            width: 100
          },
          {
            title: 'Location',
            dataIndex: 'location',
            key: 'location',
            ellipsis: true,
            // allow flex growth
          },
          {
            title: 'Event',
            dataIndex: 'event',
            key: 'event',
            ellipsis: true,
            // allow flex growth
          },
          {
            title: 'Blood Pressure',
            dataIndex: 'bp',
            key: 'bp',
            render: (bp) => {
              if (bp === '-') return <span style={{ color: '#999' }}>-</span>;
              const [systolic] = bp.split('/').map(Number);
              const color = systolic >= 140 ? '#ff4d4f' : systolic >= 130 ? '#faad14' : '#52c41a';
              return <span style={{ color, fontWeight: 600 }}>{bp}</span>;
            },
            width: 140
          },
          {
            title: 'Heart Rate',
            dataIndex: 'hr',
            key: 'hr',
            render: (hr) => {
              if (hr === '-') return <span style={{ color: '#999' }}>-</span>;
              const color = hr > 100 || hr < 60 ? '#faad14' : '#52c41a';
              return <span style={{ color, fontWeight: 600 }}>{hr}</span>;
            },
            width: 110
          },
          {
            title: 'Total Chol.',
            dataIndex: 'cholesterol',
            key: 'cholesterol',
            render: (chol) => {
              if (chol === '-') return <span style={{ color: '#999' }}>-</span>;
              const color = chol >= 240 ? '#ff4d4f' : chol >= 200 ? '#faad14' : '#52c41a';
              return <span style={{ color, fontWeight: 600 }}>{chol}</span>;
            },
            width: 100
          },
          {
            title: 'HDL',
            dataIndex: 'hdl',
            key: 'hdl',
            render: (hdl) => {
              if (hdl === '-') return <span style={{ color: '#999' }}>-</span>;
              const color = hdl < 40 ? '#ff4d4f' : hdl < 60 ? '#faad14' : '#52c41a';
              return <span style={{ color, fontWeight: 600 }}>{hdl}</span>;
            },
            width: 90
          },
          {
            title: 'LDL',
            dataIndex: 'ldl',
            key: 'ldl',
            render: (ldl) => {
              if (ldl === '-') return <span style={{ color: '#999' }}>-</span>;
              const color = ldl >= 160 ? '#ff4d4f' : ldl >= 130 ? '#faad14' : '#52c41a';
              return <span style={{ color, fontWeight: 600 }}>{ldl}</span>;
            },
            width: 90
          },
          {
            title: 'A1C',
            dataIndex: 'a1c',
            key: 'a1c',
            render: (a1c) => {
              if (a1c === '-') return <span style={{ color: '#999' }}>-</span>;
              const color = a1c >= 6.5 ? '#ff4d4f' : a1c >= 5.7 ? '#faad14' : '#52c41a';
              return <span style={{ color, fontWeight: 600 }}>{a1c}%</span>;
            },
            width: 80
          }
        ]}
        pagination={false}
        size="middle"
        scroll={{ x: true }}
        rowClassName={() => 'clickable-history-row'}
        onRow={(record) => ({
          onClick: () => {
            if (record.visitId) {
              onSelectVisit && onSelectVisit(record.visitId);
            }
          }
        })}
      />
    </div>
  );
};

export default VisitHistoryTab;
